import React, { useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';

import { Mui } from 'components';

const useStyles = Mui.makeStyles(theme => ({
  '@keyframes sweep': {
    from: { transform: 'scaleX(0)' },
    to: { transform: 'scaleX(0.85)' },
  },
  root: {
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: ({ base }) => base,
    width: '-webkit-fill-available',
    minWidth: '100%',
    height: 2,
    minHeight: 2,
    content: '',
  },
  bar: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    backgroundColor: ({ target }) => target,
  },
  barLoading: {
    animation: '$sweep 4000ms cubic-bezier(0.15, 0.65, 0.35, 1) forwards',
  },
  barFinishing: {
    transform: 'scaleX(1)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.short,
    }),
  },
}));

function Repeater(props) {
  const { color, loading } = props;

  const theme = Mui.useTheme();
  const target = color || theme.palette.primary.main;

  // States
  const [base, setBase] = useState(target);
  const [phase, setPhase] = useState('idle');

  const barRef = useRef(null);
  const classes = useStyles({ base, target });

  useEffect(() => {
    if (target !== base && phase === 'idle') setPhase('loading');
    if (target === base && phase !== 'idle') setPhase('idle');
  }, [target, base, phase]);

  useEffect(() => {
    if (phase !== 'loading' || loading) return;

    const node = barRef.current;
    if (node) {
      const { transform } = getComputedStyle(node);
      node.style.transform = transform === 'none' ? 'scaleX(0)' : transform;
      node.getBoundingClientRect();
    }
    setPhase('finishing');
    requestAnimationFrame(() => {
      if (barRef.current) barRef.current.style.transform = '';
    });
  }, [phase, loading]);

  const handleTransitionEnd = event => {
    if (event.propertyName !== 'transform' || phase !== 'finishing') return;
    setBase(target);
    setPhase('idle');
  };

  return (
    <div className={classes.root}>
      {phase !== 'idle' && (
        <div
          ref={barRef}
          className={clsx(
            classes.bar,
            phase === 'loading' && classes.barLoading,
            phase === 'finishing' && classes.barFinishing,
          )}
          onTransitionEnd={handleTransitionEnd}
        />
      )}
    </div>
  );
}

Repeater.propTypes = {
  color: PropTypes.string,
  loading: PropTypes.bool,
};

export default Repeater;
